import {
  AGE,
  CITY,
  CREATED_AT,
  EMAIL,
  NAME,
  PHONE_NUMBER,
} from 'src/common/constants/participantSortKeys';
import { EOrder } from 'src/interfaces/Order';

export const PARTICIPANT_LIST_QUERY_KEYS = {
  editionId: 'editionId',
  ownerUserId: 'ownerUserId',
  q: 'q',
  competition: 'competition',
  distance: 'distance',
  checkedIn: 'checkedIn',
  registeredFrom: 'registeredFrom',
  registeredTo: 'registeredTo',
  orderBy: 'orderBy',
  order: 'order',
} as const;

const VALID_ORDER_BY = new Set([NAME, AGE, CITY, EMAIL, PHONE_NUMBER, CREATED_AT]);
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export type ParticipantListQueryState = {
  query: string;
  competitionFilter: string[];
  distanceFilter: string[];
  checkedInFilter: string[];
  registeredFrom: string | null;
  registeredTo: string | null;
  /** Present in URL when key exists; `undefined` when `editionId` is absent from the query string. */
  editionFilter: string | null | undefined;
  ownerUserIdFilter: string | null;
  order: EOrder;
  orderByKey: string;
};

export function parseCsvParam(value: string | null): string[] {
  if (!value) {
    return [];
  }
  return value
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
}

export function serializeCsvParam(values: string[] | null | undefined): string | null {
  if (!values?.length) {
    return null;
  }
  return values.join(',');
}

/** Accepts ISO datetimes or legacy `YYYY-MM-DD` day keys. */
export function parseDateTimeParam(value: string | null): string | null {
  if (!value) {
    return null;
  }
  if (DATE_ONLY_RE.test(value)) {
    return value;
  }
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) {
    return null;
  }
  return value;
}

export function parseParticipantListSearchParams(
  searchParams: URLSearchParams,
): ParticipantListQueryState {
  const orderByParam = searchParams.get(PARTICIPANT_LIST_QUERY_KEYS.orderBy);
  const orderParam = searchParams.get(PARTICIPANT_LIST_QUERY_KEYS.order);

  return {
    query: searchParams.get(PARTICIPANT_LIST_QUERY_KEYS.q) ?? '',
    competitionFilter: parseCsvParam(searchParams.get(PARTICIPANT_LIST_QUERY_KEYS.competition)),
    distanceFilter: parseCsvParam(searchParams.get(PARTICIPANT_LIST_QUERY_KEYS.distance)),
    checkedInFilter: parseCsvParam(searchParams.get(PARTICIPANT_LIST_QUERY_KEYS.checkedIn)),
    registeredFrom: parseDateTimeParam(searchParams.get(PARTICIPANT_LIST_QUERY_KEYS.registeredFrom)),
    registeredTo: parseDateTimeParam(searchParams.get(PARTICIPANT_LIST_QUERY_KEYS.registeredTo)),
    editionFilter: searchParams.has(PARTICIPANT_LIST_QUERY_KEYS.editionId)
      ? searchParams.get(PARTICIPANT_LIST_QUERY_KEYS.editionId) || null
      : undefined,
    ownerUserIdFilter: searchParams.get(PARTICIPANT_LIST_QUERY_KEYS.ownerUserId),
    orderByKey: orderByParam && VALID_ORDER_BY.has(orderByParam) ? orderByParam : NAME,
    order: orderParam === EOrder.desc ? EOrder.desc : EOrder.asc,
  };
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null | undefined) {
  if (value == null || value === '') {
    params.delete(key);
  } else {
    params.set(key, value);
  }
}

export type ParticipantListFilterPatch = Partial<{
  query: string;
  competitionFilter: string[];
  distanceFilter: string[];
  checkedInFilter: string[];
  registeredFrom: string | null;
  registeredTo: string | null;
  editionFilter: string | null;
  ownerUserIdFilter: string | null;
  order: EOrder;
  orderByKey: string;
}>;

export function applyParticipantListFilterToSearchParams(
  prev: URLSearchParams,
  patch: ParticipantListFilterPatch,
): URLSearchParams {
  const next = new URLSearchParams(prev);

  if ('query' in patch) {
    setOrDelete(next, PARTICIPANT_LIST_QUERY_KEYS.q, patch.query);
  }
  if ('competitionFilter' in patch) {
    setOrDelete(next, PARTICIPANT_LIST_QUERY_KEYS.competition, serializeCsvParam(patch.competitionFilter));
  }
  if ('distanceFilter' in patch) {
    setOrDelete(next, PARTICIPANT_LIST_QUERY_KEYS.distance, serializeCsvParam(patch.distanceFilter));
  }
  if ('checkedInFilter' in patch) {
    setOrDelete(next, PARTICIPANT_LIST_QUERY_KEYS.checkedIn, serializeCsvParam(patch.checkedInFilter));
  }
  if ('registeredFrom' in patch) {
    setOrDelete(next, PARTICIPANT_LIST_QUERY_KEYS.registeredFrom, patch.registeredFrom);
  }
  if ('registeredTo' in patch) {
    setOrDelete(next, PARTICIPANT_LIST_QUERY_KEYS.registeredTo, patch.registeredTo);
  }
  if ('editionFilter' in patch) {
    setOrDelete(next, PARTICIPANT_LIST_QUERY_KEYS.editionId, patch.editionFilter);
  }
  if ('ownerUserIdFilter' in patch) {
    setOrDelete(next, PARTICIPANT_LIST_QUERY_KEYS.ownerUserId, patch.ownerUserIdFilter);
  }
  if ('orderByKey' in patch && patch.orderByKey) {
    if (patch.orderByKey === NAME) {
      next.delete(PARTICIPANT_LIST_QUERY_KEYS.orderBy);
    } else {
      next.set(PARTICIPANT_LIST_QUERY_KEYS.orderBy, patch.orderByKey);
    }
  }
  if ('order' in patch && patch.order) {
    if (patch.order === EOrder.asc) {
      next.delete(PARTICIPANT_LIST_QUERY_KEYS.order);
    } else {
      next.set(PARTICIPANT_LIST_QUERY_KEYS.order, patch.order);
    }
  }

  return next;
}
